import type { ReactNode } from "react";

import { ErrorState } from "@/components/ErrorState";

/** react-query 结果的最小公共面；便于把不同 hook 归一到一次状态判断。 */
export interface RemoteQueryLike {
  isPending: boolean;
  isError: boolean;
  error: unknown;
}

export interface RemoteStateProps {
  /** 该视图依赖的查询集合；任一失败即整块呈现错误态。 */
  queries: RemoteQueryLike[];
  /** 错误态标题，如「总览加载失败」。 */
  title: string;
  /** 加载态文案；留空则使用骨架文案。 */
  loadingText?: string;
  children: ReactNode;
}

/**
 * 视图级远端状态边界（RULES §11.2）。
 *
 * 统一处理「加载中 / 失败 / 就绪」三态，取代原先的静默回落。
 * 失败时**不渲染任何业务数据**——这是 AC-1401 的落点。
 */
export function RemoteState({ queries, title, loadingText, children }: RemoteStateProps) {
  const failed = queries.find((query) => query.isError);
  if (failed) {
    return <ErrorState title={title} error={failed.error} />;
  }
  if (queries.length > 0 && queries.every((query) => query.isPending)) {
    return (
      <div className="empty-state">
        <strong>{loadingText ?? "加载中"}</strong>
      </div>
    );
  }
  return <>{children}</>;
}
